import { useId } from 'react';

/**
 * The QFly lockup — gradient "Q" monogram plus "Fly" wordmark, ported
 * verbatim from the approved brand asset (51×21 artboard, paths
 * unmodified) so every surface in the app (top bar, sidebar) renders the
 * exact same mark instead of three divergent logo files.
 *
 * The wordmark paths use `currentColor` so the mark inherits whatever ink
 * color its container is set to (e.g. dark text on the sidebar's light
 * background). The "Q" monogram's gradient stops are brand-constant
 * literals, not theme tokens — the same two hex values in every context.
 *
 * `useId()` gives the `<linearGradient>` a unique id per mounted
 * instance — required because the mark can render more than once on a
 * page (e.g. top bar + a marketing/empty-state surface) and SVG
 * `id`s are document-global.
 */
export interface BrandMarkProps {
  /** Rendered height in px; width follows the artboard's 51:21 aspect ratio. */
  height?: number;
  /** Accessible name. Pass '' when adjacent text already names it. */
  title?: string;
}

const RATIO = 51 / 21;
const GRADIENT_FROM = '#0060e5';
const GRADIENT_TO = '#00aeff';

export function BrandMark({ height = 21, title = 'QFly' }: BrandMarkProps): JSX.Element {
  const gradientId = useId();

  return (
    <svg
      width={Math.round(height * RATIO)}
      height={height}
      viewBox="0 0 51 21"
      fill="none"
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
    >
      {/* "Fly" — inherits the surrounding text color. */}
      <path
        fill="currentColor"
        d="M40.6822 20.2422C40.0942 20.2422 39.5132 20.1511 38.9392 19.9691C38.3652 19.7871 37.8962 19.5351 37.5322 19.2131L38.7292 16.8821C38.9812 17.1061 39.2682 17.2811 39.5902 17.4071C39.9262 17.5331 40.2552 17.5962 40.5772 17.5962C41.0392 17.5962 41.4032 17.4841 41.6692 17.2601C41.9492 17.0501 42.2012 16.6931 42.4252 16.1891L43.0132 14.8031L43.2652 14.4461L47.3182 4.70215H50.4682L45.3652 16.6932C45.0012 17.6032 44.5812 18.3172 44.1052 18.8352C43.6432 19.3532 43.1252 19.7171 42.5512 19.9271C41.9912 20.1371 41.3682 20.2422 40.6822 20.2422ZM42.6142 16.4411L37.5742 4.70215H40.9552L44.8612 14.1522L42.6142 16.4411Z"
      />
      <path fill="currentColor" d="M32.938 16.0001V0.418091H36.214V16.0001H32.938Z" />
      <path
        fill="currentColor"
        d="M19.7568 16.0002V1.30017H30.8658V4.03017H23.1588V16.0002H19.7568ZM22.9068 10.6452V7.91517H29.9628V10.6452H22.9068Z"
      />
      {/* The "Q" monogram — brand gradient, held constant across contexts. */}
      <path
        fill={`url(#${gradientId})`}
        d="M2.48926 2.49049C5.80837 -0.830041 11.1926 -0.829959 14.5117 2.49049C16.7779 4.75762 17.4961 7.98658 16.668 10.8626L13.5869 9.39283C13.8655 7.79058 13.3889 6.08228 12.1543 4.84791C10.1396 2.83278 6.8614 2.83278 4.84668 4.84791C2.83216 6.86119 2.83203 10.1385 4.84668 12.1536C6.08137 13.3879 7.7902 13.8644 9.39258 13.5862L10.8613 16.6653V16.6672C7.98353 17.4963 4.75548 16.7791 2.48926 14.512C-0.829685 11.1914 -0.829821 5.80912 2.48926 2.49049ZM16.8682 14.677L14.6758 16.8674L8.16699 10.3587L10.3584 8.16822L16.8682 14.677Z"
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="13.1665"
          y1="14.0001"
          x2="3.6665"
          y2="4.50012"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={GRADIENT_FROM} />
          <stop offset="1" stopColor={GRADIENT_TO} />
        </linearGradient>
      </defs>
    </svg>
  );
}
